import { Button } from 'antd'
import {
  ADVICEPREP_ENTRY_CONNECTIONS,
  ADVICEPREP_TRIAL_CONNECTIONS,
  advicePrepCommittedMonthly,
  advicePrepCouponCopy,
  advicePrepCouponDiscountApplied,
  advicePrepDueToday,
  advicePrepTrialMonths,
  estimateAdvicePrepUsage,
  formatAdvicePrepCurrency,
  normalizeConnections,
} from '@/features/adviceprep/pricing'
import { useSignup } from '../SignupContext'

export default function AdvicePrepOrderSummary() {
  const { advicePrepConnections, pricingForm, couponValid, advicePrepCoupon, setStep, loading } = useSignup()

  const connections = normalizeConnections(advicePrepConnections)
  const committed = advicePrepCommittedMonthly()
  const discount = advicePrepCouponDiscountApplied(couponValid, advicePrepCoupon)
  const dueToday = advicePrepDueToday(couponValid, advicePrepCoupon)
  const estimatedUsage = estimateAdvicePrepUsage(connections)
  const renewalNote = discount > 0 ? advicePrepCouponCopy(advicePrepCoupon)?.renewal ?? null : null
  const trialMonths = couponValid ? advicePrepTrialMonths(advicePrepCoupon) : null
  const afterTrialNote = trialMonths ? (
    <span className="block text-[0.6875rem] text-gray-500">Applies after your trial ends</span>
  ) : null

  return (
    <div className="rounded-[0.5rem] border border-gray-200 bg-gray-50 px-[1rem] py-[0.875rem]">
      <div className="mb-[0.625rem] flex items-center justify-between text-[0.84375rem] font-semibold text-gray-900">
        <span>Order summary</span>
        <Button type="link" size="small" className="h-auto p-0 text-[0.75rem]" disabled={loading} onClick={() => setStep('pricing')}>
          Edit
        </Button>
      </div>

      {trialMonths && (
        <div className="mb-[0.375rem] flex justify-between text-[0.8125rem] text-gray-700">
          <span>Free trial — {ADVICEPREP_TRIAL_CONNECTIONS} connections</span>
          <span>
            {trialMonths} {trialMonths > 1 ? 'months' : 'month'}
          </span>
        </div>
      )}

      <div className="mb-[0.375rem] flex justify-between text-[0.8125rem] text-gray-700">
        <span>
          AdvicePrep — 1 to {ADVICEPREP_ENTRY_CONNECTIONS} connections
          {afterTrialNote}
        </span>
        <span>{formatAdvicePrepCurrency(committed)}</span>
      </div>

      <div className="mb-[0.375rem] flex justify-between text-[0.8125rem] text-gray-700">
        <span>
          Connections beyond {ADVICEPREP_ENTRY_CONNECTIONS}
          {afterTrialNote}
        </span>
        <span className="text-gray-500">billed on usage</span>
      </div>

      <div className="mb-[0.375rem] flex justify-between text-[0.8125rem] text-gray-700">
        <span>Billed monthly · AUD</span>
        <span className="text-gray-500">GST ex.</span>
      </div>

      {couponValid && (
        <div className="mb-[0.375rem] flex justify-between text-[0.8125rem] text-emerald-600">
          <span>Promo code ({pricingForm.couponCode})</span>
          <span>
            {discount > 0 ? `−${formatAdvicePrepCurrency(discount, 2)}` : 'applied at billing'}
          </span>
        </div>
      )}

      <div className="mt-[0.5rem] flex items-baseline justify-between border-t border-gray-200 pt-[0.5rem] text-[0.90625rem] font-bold text-gray-900">
        <span>Due today</span>
        <span>
          {discount > 0 && (
            <s className="mr-[0.5rem] font-normal text-gray-400">{formatAdvicePrepCurrency(committed, 2)}</s>
          )}
          {formatAdvicePrepCurrency(dueToday, 2)}
        </span>
      </div>

      {renewalNote && <div className="mt-[0.375rem] text-[0.75rem] text-gray-500">{renewalNote}</div>}

      {estimatedUsage > 0 && (
        <div className="mt-[0.375rem] text-[0.75rem] leading-[1.5] text-gray-500">
          {trialMonths ? 'After your trial, at' : 'At'} {connections} connections you would add about {formatAdvicePrepCurrency(estimatedUsage)} of usage
          a month. Usage is measured from the organisations you actually connect and billed in arrears.
        </div>
      )}
    </div>
  )
}
